import { Accordion, AccordionDetails, AccordionSummary, Box, Stack, Typography } from '@montugroup/design-system';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useTheme } from '@mui/material';
import type { PropsWithChildren, ReactNode } from 'react';

export type Props = {
  title: string;
  TitleAdornment?: ReactNode;
  defaultExpanded?: boolean;
};

function CollapsibleTableWrapper({ title, TitleAdornment, defaultExpanded, children }: PropsWithChildren<Props>) {
  const theme = useTheme();
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      sx={{
        margin: '0.25rem 0',
        '&.Mui-expanded': {
          margin: '0 !important',
        },
      }}
    >
      <AccordionSummary
        expandIcon={<KeyboardArrowUpIcon color="secondary" />}
        sx={{
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.common.white,
          position: 'sticky',
          top: 70, // height of header + small gap
          zIndex: 1,
          minHeight: '40px',
          transition: 'none',
          // This creates a white space to fill the small gap between the header and accordion summary to cover
          // table content when scrolling
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -8,
            left: 0,
            right: 0,
            height: '8px',
            background: 'white',
            zIndex: 1,
          },
          '&.Mui-expanded': {
            minHeight: '40px',
            maxHeight: '40px',
            margin: '0.25rem 0',
          },
          '& .MuiAccordionSummary-content': {
            margin: 0,
            transition: 'none',
            '& .Mui-expanded': {
              margin: '0 !important',
            },
          },
        }}
      >
        <Stack direction="row" alignItems="center">
          <Box sx={{ minWidth: '12rem' }} paddingX={2}>
            <Typography variant="subtitle2">{title}</Typography>
          </Box>
          {TitleAdornment}
        </Stack>
      </AccordionSummary>
      {children && <AccordionDetails>{children}</AccordionDetails>}
    </Accordion>
  );
}

export default CollapsibleTableWrapper;
